import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Check, Flame, X } from 'lucide-react';
import { featureValue } from './SubscriptionCard';
import { loadSubscriptions, createSubscriberSubscription } from '../../utils/subscriptionApi';
import useModalDialog from '../../utils/useModalDialog';
import './IgniteOfferPopup.css';

export default function IgniteOfferPopup() {
  const location = useLocation();
  if (/^\/(admin|login|lms|player)(\/|$)/i.test(location.pathname)) return null;
  return <IgniteOfferContent key={location.key} />;
}

function IgniteOfferContent() {
  const [isOpen, setIsOpen] = useState(false);
  const [catalogue, setCatalogue] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [retry, setRetry] = useState(0);
  const [form, setForm] = useState({ name: '', mobile: '', email: '', consent: false });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ type: '', message: '' });
  const dialogRef = useModalDialog(isOpen, '#ignite-offer-name');
  const submitting = status.type === 'sending';

  useEffect(() => {
    let timer;
    const openWhenAvailable = () => {
      if (document.querySelector('dialog[open], [aria-modal="true"]:not(dialog)')) {
        timer = setTimeout(openWhenAvailable, 500);
      } else {
        setIsOpen(true);
      }
    };
    timer = setTimeout(openWhenAvailable, 5000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    let active = true;
    setLoadError('');
    loadSubscriptions().then(data => {
      if (!data.plans.some(plan => plan.id === 'ignite')) throw new Error('Ignite benefits are unavailable. Please retry.');
      if (active) setCatalogue(data);
    }).catch(error => {
      if (active) setLoadError(error.message);
    });
    return () => { active = false; };
  }, [retry]);

  const close = () => setIsOpen(false);
  const change = event => {
    const { name, value, type, checked } = event.target;
    setForm(current => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
    setErrors(current => ({ ...current, [name]: '' }));
    setStatus({ type: '', message: '' });
  };
  const submit = async event => {
    event.preventDefault();
    if (submitting) return;
    const invalid = {};
    if (!form.name.trim()) invalid.name = 'Enter your name.';
    if (!/^\+?[0-9]{10,15}$/.test(form.mobile.trim())) invalid.mobile = 'Enter 10–15 digits, optionally starting with +.';
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) invalid.email = 'Enter a valid email address or leave it blank.';
    if (!form.consent) invalid.consent = 'Please consent to being contacted about this offer.';
    setErrors(invalid);
    if (Object.keys(invalid).length) {
      dialogRef.current.querySelector(`#ignite-offer-${Object.keys(invalid)[0]}`).focus();
      return;
    }
    setStatus({ type: 'sending', message: '' });
    try {
      const subscription = await createSubscriberSubscription(form, 'ignite', 'ignite_free_3_months');
      setStatus({ type: 'success', message: `Your free Ignite subscription is active from ${subscription.startDate} through ${subscription.endDate}. No payment has been collected and there is no automatic renewal.` });
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    }
  };
  const benefits = catalogue?.featureGroups.flatMap(group => group.features).filter(feature =>
    feature.type !== 'pending' && feature.values.ignite !== false && feature.values.ignite !== 0
  ) || [];

  return (
    <dialog ref={dialogRef} className="ignite-offer" aria-labelledby="ignite-offer-title" aria-describedby="ignite-offer-description"
      onCancel={event => { event.preventDefault(); close(); }}
      onKeyDown={event => { if (event.key === 'Escape') { event.preventDefault(); close(); } }}
      onClick={event => {
        if (event.target !== event.currentTarget) return;
        const bounds = event.currentTarget.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) close();
      }}>
      <button className="ignite-offer-close" type="button" onClick={close} aria-label="Close Ignite offer"><X size={22} aria-hidden="true" /></button>
      <div className="ignite-offer-intro">
        <span className="ignite-offer-icon"><Flame size={28} aria-hidden="true" /></span>
        <p className="ignite-offer-eyebrow">YOUR NEXT CHAPTER STARTS HERE</p>
        <h2 id="ignite-offer-title">Join Ignite.<br /><span>Free for 3 months.</span></h2>
        <p id="ignite-offer-description">Build momentum with learning, community and personal mentoring. Request your first 3 months free.</p>
        {catalogue ? <>
          <ul className="ignite-offer-benefits">{benefits.map(feature => <li key={feature.id}><Check size={17} aria-hidden="true" /><span>{feature.label}{feature.type !== 'included' && ` — ${featureValue(feature, 'ignite')}`}</span></li>)}</ul>
          <p className="ignite-offer-fine-print">{catalogue.page.discountTerms} {catalogue.page.certificationTerms}</p>
        </> : loadError ? <p role="alert">{loadError} <button type="button" className="ignite-offer-retry" onClick={() => setRetry(value => value + 1)}>Retry benefits</button></p> : <p role="status">Loading Ignite benefits…</p>}
        <Link className="ignite-offer-more" to="/subscriptions#subscription-plans" onClick={() => {
          close();
          document.getElementById('subscription-plans')?.scrollIntoView?.({ block: 'start' });
        }}>Know more about Ignite <span aria-hidden="true">→</span></Link>
      </div>
      <div className="ignite-offer-form">
        <h3>Start your free 3 months</h3>
        <p>Leave your details. Our team will help you get started.</p>
        {status.type === 'success' ? <p role="status" className="ignite-offer-success">{status.message}</p> : <form noValidate onSubmit={submit}>
          <fieldset disabled={submitting}>
            <legend className="ignite-offer-sr-only">Your Ignite offer request</legend>
            {[['name', 'Your name', 'text', 'name'], ['mobile', 'Mobile number', 'tel', 'tel'], ['email', 'Email address (optional)', 'email', 'email']].map(([name, label, type, autoComplete]) => <div key={name}>
              <label htmlFor={`ignite-offer-${name}`}>{label}{name !== 'email' && <span aria-hidden="true"> *</span>}</label>
              <input id={`ignite-offer-${name}`} name={name} type={type} autoComplete={autoComplete} required={name !== 'email'} value={form[name]} onChange={change} aria-invalid={Boolean(errors[name])} aria-describedby={errors[name] ? `ignite-offer-${name}-error` : undefined} />
              {errors[name] && <span role="alert" className="ignite-offer-error" id={`ignite-offer-${name}-error`}>{errors[name]}</span>}
            </div>)}
            <label className="ignite-offer-consent"><input id="ignite-offer-consent" name="consent" type="checkbox" checked={form.consent} onChange={change} required aria-invalid={Boolean(errors.consent)} aria-describedby={errors.consent ? 'ignite-offer-consent-error' : undefined} /><span>I agree to start my free Ignite subscription today and to be contacted about it.</span></label>
            {errors.consent && <span role="alert" className="ignite-offer-error" id="ignite-offer-consent-error">{errors.consent}</span>}
            <button className="ignite-offer-submit" type="submit">{submitting ? 'Sending request…' : 'Start 3 free months'}</button>
          </fieldset>
          {status.type === 'error' && <p role="alert" className="ignite-offer-error">{status.message}</p>}
        </form>}
        <p className="ignite-offer-fine-print">Submitting activates 3 free calendar months of Ignite starting today. No payment or automatic paid renewal. Any continuation after 3 months will be discussed with you.</p>
      </div>
    </dialog>
  );
}
